import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useContext, useMemo, useState } from 'react';
import { useToastNotification, useLocalizeMessage } from 'libs.nucleus';
import { OrgContext } from '../../contexts/org/org.context';
import { SiteContext } from '../../contexts/site';
import { UserLocaleContext } from '../../contexts/user_locale/user_locale.context';
import { getApiInstance, getApiErrorMessage } from '../../helpers/api.helpers';
import { useCurrentEnvironment } from '../use_current_environment';
export var EParticipantCategory;
(function (EParticipantCategory) {
    EParticipantCategory["ACTIVE"] = "Active";
    EParticipantCategory["DEACTIVATED"] = "Deactivated";
})(EParticipantCategory || (EParticipantCategory = {}));
export const useFetchParticipants = ({ initialPage = 0, initialPageSize = 10, initialCategory = EParticipantCategory.ACTIVE, }) => {
    const { environment } = useCurrentEnvironment();
    const { participantStatusMap } = useContext(OrgContext);
    const { site } = useContext(SiteContext);
    const { getUserSelectedLocale } = useContext(UserLocaleContext);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const userSelectedLocale = getUserSelectedLocale();
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [pagination, setPagination] = useState({ pageIndex: initialPage, pageSize: initialPageSize });
    const [searchValue, setSearchValue] = useState(undefined);
    const [category, setCategory] = useState(initialCategory);
    const participantStatuses = useMemo(() => {
        return Array.from(participantStatusMap.keys());
    }, [participantStatusMap]);
    const fetchParticipants = async (params) => {
        const { pageSize, pageIndex, filters } = params;
        if (!environment) {
            return [];
        }
        const activeParticipantStatuses = participantStatuses.filter((status) => status !== EParticipantCategory.DEACTIVATED);
        const whereClause = {
            c_type: { $in: ['participant', null] },
            c_status: { $in: activeParticipantStatuses },
        };
        if (filters.searchValue) {
            whereClause.c_number = { $eq: filters.searchValue };
        }
        if (filters.category === EParticipantCategory.DEACTIVATED) {
            whereClause.c_status = { $eq: filters.category };
        }
        try {
            const { data: response } = await getApiInstance(environment, userSelectedLocale).get(`/v2/routes/site_subjects/${site?._id}`, {
                params: {
                    enablePagination: true,
                    sort: JSON.stringify({ created: -1 }),
                    where: JSON.stringify(whereClause),
                    limit: pageSize,
                    skip: pageIndex * pageSize,
                },
            });
            // the response is different from other axon endpoints b/c we needed to put total number of objects in the response
            if (response?.data?.data?.length === 0) {
                setTotalPages(0);
                setTotalItems(0);
                return [];
            }
            setTotalItems(response?.data?.total || 0);
            setTotalPages(Math.ceil((response?.data?.total || 0) / pagination.pageSize));
            return response?.data?.data || [];
        }
        catch (error) {
            const message = getApiErrorMessage(error);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
            return [];
        }
    };
    const { data: participants, isLoading, isFetching, } = useQuery({
        queryKey: [
            'participantsList',
            environment?.id,
            site?._id,
            userSelectedLocale,
            { pagination, searchValue, category },
        ],
        queryFn: () => fetchParticipants({
            pageSize: pagination.pageSize,
            pageIndex: pagination.pageIndex,
            filters: { searchValue, category },
        }),
        enabled: true,
        placeholderData: keepPreviousData,
    });
    return {
        isLoading,
        isFetching,
        participants,
        totalPages,
        totalItems,
        pagination,
        setPagination,
        setSearchValue,
        setCategory,
    };
};
